import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Plus, Loader2, X } from "lucide-react";
import { api } from "../lib/api";

const COLUMNS = [
    { key: "todo", label: "To do" },
    { key: "in_progress", label: "In progress" },
    { key: "review", label: "In review" },
    { key: "done", label: "Done" },
];

const URGENCY_COLOR = {
    low: "text-zinc-400 border-white/[0.08] bg-white/[0.02]",
    medium: "text-sky-300 border-sky-500/30 bg-sky-500/10",
    high: "text-amber-300 border-amber-500/30 bg-amber-500/10",
    urgent: "text-red-300 border-red-500/30 bg-red-500/10",
};

const CAT_COLOR = {
    website: "#0066FF",
    seo: "#10B981",
    social: "#FF5500",
    leads: "#F59E0B",
    branding: "#A78BFA",
    ai: "#0066FF",
    funnel: "#FF5500",
    email: "#10B981",
    support: "#71717A",
};

function TaskCard({ task, onUpdate }) {
    return (
        <motion.div
            layout
            data-testid={`task-card-${task.id}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="elate-card p-4 cursor-grab hover:border-white/20 transition group"
        >
            <div className="flex items-center justify-between mb-2.5">
                <span
                    className="text-[10px] uppercase tracking-[0.18em] font-mono px-2 py-0.5 rounded-full border"
                    style={{
                        borderColor: `${CAT_COLOR[task.category] || "#71717A"}40`,
                        color: CAT_COLOR[task.category] || "#A1A1AA",
                        background: `${CAT_COLOR[task.category] || "#71717A"}10`,
                    }}
                >
                    {task.category}
                </span>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${URGENCY_COLOR[task.urgency] || URGENCY_COLOR.low}`}>
                    {task.urgency}
                </span>
            </div>
            <div className="text-sm leading-snug">{task.title}</div>
            {task.source === "whatsapp" && (
                <div className="text-[10px] text-emerald-400 mt-2 font-mono">via WhatsApp</div>
            )}
            <div className="mt-3 flex items-center justify-between">
                <select
                    data-testid={`task-status-${task.id}`}
                    value={task.status}
                    onChange={(e) => onUpdate(task.id, { status: e.target.value })}
                    className="bg-transparent text-[10px] text-zinc-400 border border-white/[0.08] rounded px-1.5 py-0.5 focus:outline-none"
                >
                    {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <span className="text-[10px] text-zinc-500 font-mono">{task.assignee}</span>
            </div>
        </motion.div>
    );
}

function CreateModal({ open, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setLoading(true);
        try {
            const { data } = await api.post("/tasks", { title, description });
            onCreated(data);
            setTitle(""); setDescription("");
            onClose();
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="task-create-modal">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <motion.form
                onSubmit={submit}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative w-full max-w-lg rounded-2xl border border-white/[0.1] bg-[#0E0E11] p-6"
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[#0066FF]" />
                        <h3 className="font-display text-lg">New task</h3>
                    </div>
                    <button type="button" data-testid="task-create-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06]">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <p className="text-xs text-zinc-500 mb-4">Ana will auto-categorise and score urgency.</p>
                <input
                    data-testid="task-create-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="What needs doing?"
                    autoFocus
                    className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none mb-3"
                />
                <textarea
                    data-testid="task-create-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add detail (optional)"
                    rows={3}
                    className="w-full bg-[#08080A] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm focus:border-[#0066FF] outline-none resize-none"
                />
                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-white/[0.08] hover:bg-white/[0.04]">Cancel</button>
                    <button
                        data-testid="task-create-submit"
                        disabled={loading}
                        className="text-xs px-4 py-2 rounded-full bg-white text-black font-medium hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        Create with Ana
                    </button>
                </div>
            </motion.form>
        </div>
    );
}

export default function Tasks() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    useEffect(() => {
        api.get("/tasks").then(({ data }) => {
            setTasks(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const onUpdate = async (id, patch) => {
        setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
        try {
            await api.patch(`/tasks/${id}`, patch);
        } catch {
            // refresh on error
            const { data } = await api.get("/tasks");
            setTasks(data);
        }
    };

    const grouped = useMemo(() => {
        const g = {};
        for (const col of COLUMNS) g[col.key] = [];
        for (const t of tasks) (g[t.status] || g.todo).push(t);
        return g;
    }, [tasks]);

    return (
        <div className="space-y-6" data-testid="tasks-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Operations</div>
                    <h2 className="font-display text-3xl mt-2">Tasks</h2>
                </div>
                <button
                    data-testid="task-new-button"
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2 text-xs font-medium hover:bg-zinc-200 transition"
                >
                    <Plus className="h-3.5 w-3.5" /> New task
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-4 gap-5">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="space-y-3">
                            {Array.from({ length: 3 }).map((__, j) => (
                                <div key={j} className="elate-card p-4 h-24 animate-pulse" />
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                    {COLUMNS.map((col) => (
                        <div key={col.key} data-testid={`task-column-${col.key}`} className="elate-card p-3 min-h-[300px]">
                            <div className="flex items-center justify-between px-2 py-2">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{col.label}</div>
                                <span className="text-xs text-zinc-500 font-mono">{grouped[col.key]?.length || 0}</span>
                            </div>
                            <div className="space-y-2 p-1">
                                {(grouped[col.key] || []).map((t) => (
                                    <TaskCard key={t.id} task={t} onUpdate={onUpdate} />
                                ))}
                                {grouped[col.key]?.length === 0 && (
                                    <div className="text-xs text-zinc-600 text-center py-8">Empty</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <CreateModal
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={(t) => setTasks((p) => [t, ...p])}
            />
        </div>
    );
}
